import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  AlertTriangle,
  Calendar,
  ExternalLink,
  MapPin,
  ClipboardList,
  Info,
} from 'lucide-react';
import {
  fetchFDAInspections,
  CDMOInspectionSummary,
  FDAInspectionRecord,
  getClassificationColor
} from '../lib/fdaService';
import { FDA_MESSAGES } from '../constants/validations';

interface FDAForm483OverlayProps {
  companyName: string;
  feiNumber?: string | string[] | null;
  isOpen: boolean;
  onClose: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  position?: { top: number; left: number };
  arrowLeft?: number;
  placement?: 'top' | 'bottom';
  contentRef?: React.RefObject<HTMLDivElement>;
}

const FDAForm483Overlay: React.FC<FDAForm483OverlayProps> = ({
  companyName,
  feiNumber,
  isOpen,
  onClose,
  onMouseEnter,
  onMouseLeave,
  position,
  arrowLeft,
  placement,
  contentRef
}) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<CDMOInspectionSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const inspectionData = await fetchFDAInspections(
          companyName,
          Array.isArray(feiNumber)
            ? feiNumber
            : feiNumber ?? undefined
        );
        setData(inspectionData);
      } catch (err: any) {
        console.error(err);
        setError(err?.message ?? FDA_MESSAGES.LOAD_ERROR);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isOpen, companyName, feiNumber]);

  useEffect(() => {
    if (!isOpen) return;
    const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isPopover = !!position;

  return (
    <AnimatePresence>
      <motion.div
        ref={contentRef}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        initial={{ opacity: 0, y: placement === 'top' ? 6 : -6, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: placement === 'top' ? -6 : 6, scale: 0.98 }}
        transition={{ type: 'spring', stiffness: 320, damping: 26 }}
        style={
          position
            ? {
              position: 'fixed',
              top: placement === 'top' ? position.top - 5 : position.top + 5,
              left: position.left,
              zIndex: 10000
            }
            : { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10000 }
        }
        className={`bg-[#F8FAFC] border border-slate-200 rounded-2xl shadow-2xl flex flex-col ${isPopover ? 'w-[calc(100vw-32px)] sm:w-[500px] md:w-[560px]' : 'w-[calc(100vw-40px)] sm:w-[90vw] md:w-full md:max-w-2xl'
          } max-h-[85vh] relative`}
      >
        {/* Arrow (Diamond) - Only in popover mode */}
        {isPopover && arrowLeft !== undefined && placement !== undefined && (
          <div
            className={`absolute ${placement === 'top' ? 'bottom-[-6px]' : 'top-[-6px]'} z-20`}
            style={{ left: arrowLeft - 6 }}
          >
            <div
              className={`w-3 h-3 rotate-45 border border-slate-200 ${placement === 'bottom' ? 'bg-white' : 'bg-white'}`}
              style={{
                boxShadow: '0 -2px 8px rgba(0,0,0,0.05)',
                ...(placement === 'top'
                  ? { borderTopColor: 'transparent', borderLeftColor: 'transparent', backgroundColor: 'white' }
                  : { borderBottomColor: 'transparent', borderRightColor: 'transparent', backgroundColor: 'white' })
              }}
            />
          </div>
        )}
        {/* Header */}
        <div className="bg-white px-6 py-5 border-b border-slate-100 relative rounded-t-2xl">
          <div className="flex justify-between items-start">
            <div className="space-y-1">
              <h2 className="text-xl font-bold text-slate-900 leading-none">
                FDA Inspection History
              </h2>
              <p className="text-[12px] text-slate-400">
                Inspection records sourced from FDA public databases
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-slate-50 text-slate-400 hover:text-slate-600 rounded-full transition-colors transition-shadow"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="p-6 overflow-y-auto max-h-[60vh] space-y-5 custom-scrollbar bg-[#f1f5f9]/20">
          {loading && (
            <div className="py-20 flex flex-col items-center justify-center text-slate-400 animate-pulse">
              <div className="w-10 h-10 border-4 border-slate-200 border-t-electric-blue rounded-full animate-spin mb-4" />
              <p className="font-medium">{FDA_MESSAGES.LOADING}</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50/50 border border-red-100 rounded-xl p-6 text-center">
              <AlertTriangle className="mx-auto mb-3 text-red-500 w-8 h-8" />
              <p className="text-red-700 font-medium mb-1">{FDA_MESSAGES.RECORDS_LOAD_FAILURE}</p>
              <p className="text-red-600/70 text-sm">{error}</p>
            </div>
          )}

          {!loading && !error && data?.inspections?.length === 0 && (
            <div className="bg-white border border-slate-200 rounded-2xl p-6 flex items-start gap-4">
              <div className="p-3 bg-slate-50 rounded-xl">
                <ClipboardList className="w-6 h-6 text-slate-400" />
              </div>
              <div>
                <h3 className="text-slate-900 font-bold text-base mb-1">{FDA_MESSAGES.NO_RECORDS_FOUND}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">
                  {FDA_MESSAGES.NO_RECORDS_DESCRIPTION}
                </p>
              </div>
            </div>
          )}

          {!loading && !error && data?.inspections?.map((inspection, idx) => (
            <InspectionCard key={idx} inspection={inspection} />
          ))}
        </div>

        {/* Fixed Footer Disclaimer */}
        {!loading && !error && (
          <div className="px-6 py-4 bg-white border-t border-slate-100 rounded-b-2xl">
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex gap-3 items-start">
              <Info className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
              <p className="text-[12px] text-blue-800 leading-relaxed font-medium">
                {FDA_MESSAGES.DISCLAIMER}
              </p>
            </div>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

/* ---------------- Helpers ---------------- */

const formatDateToMMYYYY = (dateStr: string | null | undefined) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${year}`;
};

/* ---------------- Inspection Card ---------------- */

const InspectionCard: React.FC<{ inspection: FDAInspectionRecord }> = ({
  inspection
}) => {
  const is483Issued = inspection?.PostedCitations?.trim()?.toLowerCase() === 'yes';
  const outcomeColors = getClassificationColor(inspection?.ClassificationCode ?? '');

  const outcomeDescription = FDA_MESSAGES.OUTCOME_LABELS[inspection?.ClassificationCode ?? ''] ?? inspection?.Classification ?? '';

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      {/* Top Section */}
      <div className="px-6 py-5 border-b border-slate-50 flex justify-between items-start">
        <div className="space-y-1.5">
          <div className="text-xs sm:text-sm font-bold text-slate-700 uppercase tracking-tight mb-1 ml-1 opacity-70">
            {inspection?.LegalName ?? ''}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-slate-900 font-bold text-sm sm:text-base">Inspection Outcome:</span>
            <span className={`font-bold text-sm sm:text-base ${outcomeColors?.text ?? 'text-slate-500'}`}>{inspection?.ClassificationCode ?? ''}</span>
          </div>
          <p className="text-xs sm:text-sm text-slate-500">{outcomeDescription}</p>
        </div>

        <div className={`px-3 py-1 rounded-lg border text-xs font-bold flex items-center gap-1.5 ${is483Issued ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-orange-50 border-orange-200 text-orange-700'}`}>
          FDA 483 Issued: {is483Issued ? 'Yes' : 'No'}
          <Info className="w-3 h-3 opacity-70" />
        </div>
      </div>

      {/* Info Grid */}
      <div className="px-6 py-5 bg-white">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-4 sm:gap-y-6 gap-x-4">
          <div className="space-y-1.5 leading-tight">
            <div className="flex items-center gap-1.5 text-slate-400">
              <span className="text-[11px] font-bold uppercase tracking-wider">Inspection ID</span>
            </div>
            <p className="text-slate-700 font-semibold text-[15px]">{inspection?.InspectionID ?? ''}</p>
          </div>
          <div className="space-y-1.5 leading-tight">
            <div className="flex items-center gap-1.5 text-slate-400">
              <span className="text-[11px] font-bold uppercase tracking-wider">FDA FEI Number</span>
            </div>
            <p className="text-slate-700 font-semibold text-[15px]">{inspection?.FEINumber ?? ''}</p>
          </div>

          <div className="space-y-1.5 leading-tight">
            <div className="flex items-center gap-1.5 text-slate-400">
              <Calendar className="w-3.5 h-3.5" />
              <span className="text-[11px] font-bold uppercase tracking-wider">Fiscal Year</span>
            </div>
            <p className="text-slate-700 font-semibold text-[15px]">{inspection?.FiscalYear ?? ''}</p>
          </div>

          <div className="space-y-1.5 leading-tight">
            <div className="flex items-center gap-1.5 text-slate-400">
              <span className="text-[11px] font-bold uppercase tracking-wider">Inspection Focus</span>
            </div>
            <p className="text-slate-700 font-semibold text-[15px]">{inspection?.ProjectArea ?? ''}</p>
          </div>

          <div className="space-y-1.5 leading-tight">
            <div className="flex items-center gap-1.5 text-slate-400">
              <span className="text-[11px] font-bold uppercase tracking-wider">Product Type Inspected</span>
            </div>
            <p className="text-slate-700 font-semibold text-[15px]">{inspection?.ProductType ?? ''}</p>
          </div>

          <div className="space-y-1.5 leading-tight">
            <div className="flex items-center gap-1.5 text-slate-400">
              <Calendar className="w-3.5 h-3.5" />
              <span className="text-[11px] font-bold uppercase tracking-wider">
                Last FDA inspection
              </span>
            </div>
            <p className="text-slate-700 font-semibold text-[15px]">
              {formatDateToMMYYYY(inspection?.InspectionEndDate)}
            </p>
          </div>

          <div className="space-y-1.5 leading-tight">
            <div className="flex items-center gap-1.5 text-slate-400">
              <span className="text-[11px] font-bold uppercase tracking-wider">Additional Dates</span>
            </div>
            <p className="text-slate-700 font-semibold text-[15px]">{inspection?.FMD145Date ? formatDateToMMYYYY(inspection.FMD145Date) : 'NA'}</p>
          </div>

        </div>
      </div>

      {/* Footer Location & Link */}
      <div className="px-5 py-4 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-start gap-2.5 w-full sm:max-w-[60%]">
          <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
          <div className="space-y-0.5">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Facility location:</p>
            <p className="text-[11px] sm:text-[12px] text-slate-600 leading-snug font-medium">
              {[inspection?.AddressLine1, inspection?.AddressLine2, inspection?.City, inspection?.State, inspection?.ZipCode].filter(Boolean).join(', ')}
              <br />
              {inspection?.CountryName ?? ''}
            </p>
          </div>
        </div>

        {inspection.FirmProfile && (
          <a
            href={inspection.FirmProfile}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-700 text-[11px] sm:text-[12px] font-bold hover:bg-slate-50 transition-colors shadow-sm w-full sm:w-auto"
          >
            View official FDA firm profile
            <ExternalLink className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          </a>
        )}
      </div>
    </div>
  );
};

export default FDAForm483Overlay;
